/**
 * FormField Molecule - Combines Input with Field
 *
 * This molecule combines the Input atom with field labels, validation,
 * and helper text for a complete form input experience.
 */

import { Input, type InputProps } from "@/components/atoms/Input"
import { Field } from "@chakra-ui/react"
import * as React from "react"

export interface FormFieldProps extends InputProps {
  // Field-specific props
  label?: React.ReactNode
  helperText?: React.ReactNode
  errorText?: React.ReactNode
  optionalText?: React.ReactNode

  // Required indicator
  showRequired?: boolean

  // Field container props
  fieldProps?: Omit<Field.RootProps, "children">
}

export const FormField = React.forwardRef<HTMLInputElement, FormFieldProps>(
  function FormField(props, ref) {
    const {
      label,
      helperText,
      errorText,
      optionalText,
      showRequired,
      fieldProps,
      isInvalid,
      isRequired,
      id,
      ...inputProps
    } = props

    // Generate ID for accessibility if not provided
    const fieldId = id || React.useId()
    const helperTextId = helperText ? `${fieldId}-helper` : undefined
    const errorTextId = errorText ? `${fieldId}-error` : undefined

    return (
      <Field.Root
        invalid={isInvalid || !!errorText}
        required={isRequired}
        {...fieldProps}
      >
        {label && (
          <Field.Label htmlFor={fieldId}>
            {label}
            {isRequired && showRequired && <Field.RequiredIndicator />}
            {!isRequired && optionalText && (
              <Field.RequiredIndicator fallback={optionalText} />
            )}
          </Field.Label>
        )}

        <Input
          ref={ref}
          id={fieldId}
          isInvalid={isInvalid || !!errorText}
          isRequired={isRequired}
          aria-describedby={
            [helperTextId, errorTextId].filter(Boolean).join(" ") || undefined
          }
          {...inputProps}
        />

        {helperText && (
          <Field.HelperText id={helperTextId}>{helperText}</Field.HelperText>
        )}

        {errorText && (
          <Field.ErrorText id={errorTextId}>{errorText}</Field.ErrorText>
        )}
      </Field.Root>
    )
  },
)

FormField.displayName = "FormField"

export default FormField
